import { NodeProject } from "projen/lib/javascript";

/**
 * Shared option for controlling the `private` field in `package.json`.
 *
 * Mixed into every configulator Node project's options interface
 * (`TypeScriptProjectOptions`, `AwsCdkProjectOptions`,
 * `MonorepoProjectOptions`) so the knob reads and behaves identically
 * across project types.
 */
export interface PackagePrivateOptions {
  /**
   * Whether to synthesize `"private": true` into `package.json`.
   *
   * When omitted, the value is derived from the release configuration: a
   * project that does not publish to a registry (`releaseToNpm` unset or
   * `false`) is marked private, and a project that does publish
   * (`releaseToNpm: true`) is not. npm refuses to publish a package
   * carrying `"private": true`, so the default makes an unpublished
   * package unpublishable by hand rather than merely un-wired from the
   * release workflow.
   *
   * Set to `false` to omit the field entirely — the escape hatch for a
   * package that is published by hand or by tooling outside projen's
   * release flow. Set to `true` to force the field on a project that has
   * no release configuration of its own.
   *
   * Setting `true` alongside `releaseToNpm: true` throws: the release
   * workflow would run `npm publish` against a package npm refuses to
   * accept, and failing at synth time beats failing mid-release.
   *
   * @default - `true` when `releaseToNpm` is not enabled, otherwise the field is omitted
   */
  readonly packagePrivate?: boolean;
}

/**
 * Resolve whether `package.json` should carry `"private": true`.
 *
 * An explicit `packagePrivate` always wins. Otherwise the field tracks
 * the inverse of `releaseToNpm` — projen throws when `releaseToNpm` is
 * set without `release`, so `releaseToNpm` alone is a sufficient signal
 * that a registry publish is actually wired up.
 */
export function resolvePackagePrivate(options: {
  readonly packagePrivate?: boolean;
  readonly releaseToNpm?: boolean;
}): boolean {
  const releaseToNpm = options.releaseToNpm ?? false;

  if (options.packagePrivate !== undefined) {
    if (options.packagePrivate && releaseToNpm) {
      throw new Error(
        'Cannot set `packagePrivate: true` together with `releaseToNpm: true`: npm refuses to publish a package marked `"private": true`. ' +
          "Drop `packagePrivate` to let it track the release configuration, or turn off `releaseToNpm`.",
      );
    }
    return options.packagePrivate;
  }

  return !releaseToNpm;
}

/**
 * Apply the resolved {@link resolvePackagePrivate} decision to a project's
 * `package.json`. Writes nothing when the package is publishable, so an
 * npm-released package renders exactly as it did before this option
 * existed.
 */
export function applyPackagePrivate(
  project: NodeProject,
  options: {
    readonly packagePrivate?: boolean;
    readonly releaseToNpm?: boolean;
  },
): void {
  if (resolvePackagePrivate(options)) {
    project.package.addField("private", true);
  }
}
